// frontend/src/components/layout/Header.tsx
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSiteSettingsStore } from '@/store/settingsStore';
import { LogIn, UserPlus, LayoutDashboard, LogOut, Loader2, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

const Header = () => {
  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);
  const navigate = useNavigate();
  const location = useLocation(); // Import and use useLocation

  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('authToken'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  // --- Logo and Title Logic ---
  let displayLogoUrl: string | null = null;
  const logoUrl = publicSettings?.site_favicon_url;
  if (logoUrl) {
    if (logoUrl.startsWith('http') || logoUrl.startsWith('/static/')) {
      displayLogoUrl = logoUrl.startsWith('/') ? `${backendBaseUrl}${logoUrl}` : logoUrl;
    } else {
      displayLogoUrl = `${backendBaseUrl}/static/${logoUrl}`;
    }
  }

  const displaySiteTitle = isLoadingSettings && !publicSettings?.site_title
    ? 'Loading...'
    : publicSettings?.site_title || 'CoreCMS';

  // --- Page Visibility for Navigation ---
  const productsPageEnabled = publicSettings?.page_products_enabled ?? true;
  const articlesPageEnabled = publicSettings?.page_articles_enabled ?? true;
  const researchPageEnabled = publicSettings?.page_research_enabled ?? true;
  const aboutPageEnabled = publicSettings?.page_about_enabled ?? true;
  const contactPageEnabled = publicSettings?.page_contact_enabled ?? true;
  const registrationPageEnabled = publicSettings?.page_registration_enabled ?? true;

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    setIsAuthenticated(false);
    setUserRole(null);
    setIsMenuOpen(false); // Close menu on logout
    navigate('/login');
  };

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Effect to check auth status from localStorage
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(!!localStorage.getItem('authToken'));
      setUserRole(localStorage.getItem('userRole'));
    };
    window.addEventListener('storage', checkAuth);
    checkAuth(); // Initial check
    return () => window.removeEventListener('storage', checkAuth);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isMenuOpen]);

  // Reusable navigation links to avoid duplication
  const navLinks = (
    <>
      <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/">Home</Link></Button>
      {productsPageEnabled && <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/products">Products</Link></Button>}
      {articlesPageEnabled && <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/articles">Articles</Link></Button>}
      {researchPageEnabled && <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/research">Research</Link></Button>}
      {aboutPageEnabled && <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/about">About</Link></Button>}
      {contactPageEnabled && <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3"><Link to="/contact">Contact</Link></Button>}
    </>
  );

  // Reusable auth buttons to avoid duplication
  const authButtons = (
    <>
      {isAuthenticated ? (
        <div className="flex flex-col md:flex-row md:items-center gap-2 w-full pt-4 md:pt-0 border-t md:border-t-0 border-border">
          {userRole === 'admin' && (
            <Button variant="ghost" asChild size="sm" className="w-full justify-start md:w-auto md:px-3">
              <Link to="/admin/dashboard"><LayoutDashboard className="mr-1 h-4 w-4" /> Admin</Link>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleLogout} className="w-full justify-center md:w-auto md:px-3 border-primary text-primary hover:bg-primary/5">
            <LogOut className="mr-1 h-4 w-4" /> Logout
          </Button>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:items-center gap-2 w-full pt-4 md:pt-0 border-t md:border-t-0 border-border">
          <Button variant="outline" size="sm" asChild className="w-full justify-center md:w-auto md:px-3">
            <Link to="/login"><LogIn className="mr-1 h-4 w-4" /> Login</Link>
          </Button>
          {registrationPageEnabled && (
            <Button variant="default" size="sm" asChild className="w-full justify-center md:w-auto md:px-3">
              <Link to="/register"><UserPlus className="mr-1 h-4 w-4" /> Register</Link>
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <header className="bg-background border-b sticky top-0 z-50">
      <nav className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo and Title */}
        <Link to="/" className="font-bold text-xl flex items-center gap-2">
          {isLoadingSettings && !displayLogoUrl && <div className="h-8 w-8 bg-muted rounded animate-pulse"></div>}
          {displayLogoUrl && <img src={displayLogoUrl} alt={`${displaySiteTitle} Logo`} className="h-8 max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
          <span>{displaySiteTitle}</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-1">
          {!isLoadingSettings && navLinks}
          <div className="pl-2">
            {isLoadingSettings ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : authButtons}
          </div>
        </div>
        
        {/* Mobile Hamburger Button */}
        <div className="md:hidden">
          <Button onClick={() => setIsMenuOpen(!isMenuOpen)} variant="ghost" size="icon">
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full h-[calc(100vh-4rem)] bg-background/95 backdrop-blur-sm z-40">
           <div className="container mx-auto p-4 flex flex-col gap-2">
             {isLoadingSettings ? (
                <div className="flex justify-center items-center h-48">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
             ) : (
                <>
                    {navLinks}
                    {authButtons}
                </>
             )}
           </div>
        </div>
      )}
    </header>
  );
};

export default Header;